// Client-safe Subscribe & Save config types + resolver. Kept separate from
// product-page-settings.ts (which imports the server-only payload client)
// because the buy box imports these values in the client bundle.

export interface SubscribeSaveBenefit {
	icon: string;
	text: string;
}

/** Free gift bundled with the first subscription order. */
export interface SubscribeSaveGift {
	imageUrl: string | null;
	imageAlt: string;
	title: string;
	description: string;
	/** Retail value shown struck through next to the FREE badge, e.g. "$19". */
	value: string;
	/** Shopify handle of the gift product; empty for display-only gifts. */
	productHandle: string;
	/**
	 * Resolved server-side from {@link productHandle} when the gift product is
	 * in stock. When set, add-to-cart with a subscription plan auto-adds this
	 * variant in the same cart mutation.
	 */
	variantId: string | null;
	/** Variant price for the optimistic cart line (checkout discounts make it free). */
	variantPrice: { amount: string; currencyCode: string } | null;
	/**
	 * True when the gift product has multiple variants — instead of auto-adding
	 * a variant, a selection sheet opens after the subscription is added.
	 */
	requiresSelection: boolean;
}

/**
 * Product-type-scoped Subscribe & Save copy (e.g. Toolbox vs. supplements).
 * Empty fields fall back to the global defaults; a gift or benefit list with
 * any rows replaces the default list wholesale.
 */
export interface SubscribeSaveProductTypeConfig {
	productType: string;
	title?: string;
	oneTimeLabel?: string;
	microcopy?: string;
	popularBadge?: string;
	planMeta?: string;
	giftsHeading?: string;
	gifts: SubscribeSaveGift[];
	giftBundleValue?: string;
	benefits: SubscribeSaveBenefit[];
}

export interface SubscribeSaveSettings {
	enabled: boolean;
	title: string;
	subscribeLabel: string;
	oneTimeLabel: string;
	/** Add-to-cart button label while a subscription plan is selected. */
	subscribeCtaLabel: string;
	/** Subtext under the CTA trust line; supports {price} and {oneTimePrice} tokens. */
	microcopy: string;
	/** Badge on the subscription card; save percent is appended at render. */
	popularBadge: string;
	/** Line under the delivery frequency, e.g. "30 servings · ships free · skip anytime". */
	planMeta: string;
	giftsHeading: string;
	gifts: SubscribeSaveGift[];
	/** Total bundle value for the summary row, e.g. "$44.00"; empty hides the row. */
	giftBundleValue: string;
	benefits: SubscribeSaveBenefit[];
	productTypes: SubscribeSaveProductTypeConfig[];
	overrides: {
		handles: string[];
		benefits: SubscribeSaveBenefit[];
		microcopy?: string;
	}[];
}

/** Per-product Subscribe & Save copy, resolved from the global settings + overrides. */
export interface SubscribeSaveConfig {
	enabled: boolean;
	title: string;
	subscribeLabel: string;
	oneTimeLabel: string;
	subscribeCtaLabel: string;
	microcopy: string;
	popularBadge: string;
	planMeta: string;
	giftsHeading: string;
	gifts: SubscribeSaveGift[];
	giftBundleValue: string;
	benefits: SubscribeSaveBenefit[];
}

export const DEFAULT_SUBSCRIBE_SAVE_SETTINGS: SubscribeSaveSettings = {
	enabled: true,
	title: 'Purchase Options',
	subscribeLabel: 'Subscribe & Save',
	oneTimeLabel: 'One-Time Purchase — no gifts',
	subscribeCtaLabel: 'Start Now',
	microcopy: 'Skip, swap, or cancel anytime.',
	popularBadge: 'Most Popular',
	planMeta: '',
	giftsHeading: 'Free gifts with your first order',
	gifts: [],
	giftBundleValue: '',
	// Rendered as the under-CTA trust line; CMS-configured benefits replace
	// these wholesale.
	benefits: [
		{ icon: 'truck', text: 'Free fast shipping' },
		{ icon: 'check', text: '30-Day Guarantee' },
		{ icon: 'refresh', text: 'Cancel anytime' },
	],
	productTypes: [],
	overrides: [],
};

/** Tolerant Shopify product-type matching (web parity: normalizeProductTypeKey). */
function normalizeTypeKey(value: string | null | undefined): string {
	return (
		value
			?.trim()
			.replace(/[‐‑–—−]/g, '-')
			.replace(/\s+/g, ' ')
			.toLowerCase() || ''
	);
}

function resolveEligibleGifts(
	handle: string,
	configuredGifts: SubscribeSaveGift[],
	giftBundleValue: string,
) {
	// The $6.50 trial does not include gift products (the tee or giveaway
	// entry). Keep display-only benefits such as free shipping, but filter
	// before enrichment so no gift lines or selection prompts can be created.
	const gifts =
		handle === 'workman-hydrate-sample-pack'
			? configuredGifts.filter((gift) => !gift.productHandle.trim() && !gift.variantId)
			: configuredGifts;
	return {
		gifts,
		// The configured total includes excluded gifts; don't advertise it.
		giftBundleValue: gifts.length === configuredGifts.length ? giftBundleValue : '',
	};
}

/**
 * The gifts a subscription started from the CART carries: the giveaway entry
 * and display-only perks such as free shipping, never merchandise. A gift that
 * is both a real product and carries a retail value (the free tee) is the
 * product page's reason to subscribe there; handing it out from the cart too
 * would make the PDP's offer worthless. The entry has a product line but no
 * price ("Chance to win"), so it stays. Apply before enrichment so no tee
 * line or size prompt can be created. The bundle value is dropped when
 * anything is filtered, since the configured total includes the tee.
 */
export function selectCartSubscriptionGifts<
	T extends { gifts: SubscribeSaveGift[]; giftBundleValue: string },
>(config: T): T {
	const gifts = config.gifts.filter(
		(gift) => !gift.productHandle.trim() || !/^[$€£]?\s*\d[\d.,]*$/.test(gift.value.trim()),
	);
	if (gifts.length === config.gifts.length) return config;
	return { ...config, gifts, giftBundleValue: '' };
}

/**
 * Resolves the Subscribe & Save copy for one product, most specific wins:
 * global defaults ← matching product-type config ← matching per-handle
 * override. A product-type gift list carries its own bundle value (an empty
 * one hides the row rather than inheriting the default gifts' total).
 */
export function resolveSubscribeSaveConfig(
	settings: SubscribeSaveSettings | null | undefined,
	productHandle: string | null | undefined,
	productType?: string | null,
): SubscribeSaveConfig {
	const base = settings ?? DEFAULT_SUBSCRIBE_SAVE_SETTINGS;
	const handle = productHandle?.trim().toLowerCase() || '';
	const typeKey = normalizeTypeKey(productType);
	const typeConfig = typeKey
		? base.productTypes.find((entry) => normalizeTypeKey(entry.productType) === typeKey)
		: undefined;
	const override = handle
		? base.overrides.find((entry) => entry.handles.includes(handle))
		: undefined;

	const hasTypeGifts = !!typeConfig && typeConfig.gifts.length > 0;
	const benefitLists = [override?.benefits, typeConfig?.benefits, base.benefits];

	return {
		enabled: base.enabled,
		title: typeConfig?.title || base.title,
		subscribeLabel: base.subscribeLabel,
		oneTimeLabel: typeConfig?.oneTimeLabel || base.oneTimeLabel,
		subscribeCtaLabel: base.subscribeCtaLabel,
		microcopy: override?.microcopy || typeConfig?.microcopy || base.microcopy,
		popularBadge: typeConfig?.popularBadge || base.popularBadge,
		planMeta: typeConfig?.planMeta || base.planMeta,
		giftsHeading: typeConfig?.giftsHeading || base.giftsHeading,
		...resolveEligibleGifts(
			handle,
			hasTypeGifts ? typeConfig.gifts : base.gifts,
			hasTypeGifts ? typeConfig.giftBundleValue || '' : base.giftBundleValue,
		),
		benefits: benefitLists.find((list) => list && list.length > 0) ?? base.benefits,
	};
}
